import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, User } from "lucide-react";
import { Logo } from "@/components/Logo";
import { AdminOffersBadge } from "@/components/AdminOffersBadge";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/beats", label: "Beats Store", id: "beats" },
  { to: "/catalog", label: "Catalog", id: "catalog" },
  { to: "/showcase", label: "Showcase", id: "showcase" },
  { to: "/photography", label: "Photography", id: "photography" },
  { to: "/videography", label: "Videography", id: "videography" },
  { to: "/booking", label: "Book Studio", id: "booking" },
  { to: "/socials", label: "Socials", id: "socials" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-xl" data-testid="main-nav">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <Logo size={36} />
          <span className="font-heading text-lg font-extrabold tracking-tight text-white">
            PRODRUSHI<span className="text-emerald-300">.</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-emerald-300/10 text-emerald-200" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <button
            data-testid="nav-account-link"
            onClick={() => navigate("/account")}
            className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300 transition-colors hover:border-emerald-300/40 hover:text-emerald-200"
          >
            <User size={15} /> Account
          </button>
          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            className="relative rounded-full bg-emerald-300 px-4 py-2 text-sm font-bold text-black transition-colors hover:bg-emerald-200"
          >
            Admin
            <AdminOffersBadge />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="rounded-lg border border-white/10 p-2 text-white lg:hidden"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {[...LINKS, { to: "/account", label: "Account", id: "account" }, { to: "/admin/login", label: "Admin", id: "admin" }].map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.id}-link`}
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-3 text-sm font-medium ${
                      isActive ? "bg-emerald-300/10 text-emerald-200" : "text-zinc-300"
                    }`
                  }
                >
                  {l.id === "admin" ? (
                    <span className="relative inline-block">
                      {l.label}
                      <AdminOffersBadge />
                    </span>
                  ) : (
                    l.label
                  )}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
